// All Admin Sub-pages
import React, { useState, useEffect, useCallback } from "react";
import { Plus, Edit2, Trash2, Search, X, Loader, RefreshCw, Save, Package, Wrench, Image as ImageIcon, CheckCircle, AlertCircle, Download, Eye, Send, ArrowUp, ArrowDown } from "lucide-react";
import { productsAPI, servicesAPI, galleryAPI, contactAPI, settingsAPI, cmsAPI, uploadAPI } from "../../services/api";
import { WysiwygEditor } from "../../components/ui/index";
import { getImageUrl } from "../../utils/image";

// ── Shared: Image Upload Button ──────────────────────────
function ImageUploadBtn({ onUploaded, type = "general", label = "Upload Image" }) {
  const [uploading, setUploading] = useState(false);
  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { data } = await uploadAPI.single(file, type);
      onUploaded(data.url);
    } catch { alert("Upload failed"); }
    finally { setUploading(false); }
  };
  return (
    <label className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 text-xs font-semibold cursor-pointer transition-colors ${uploading ? "opacity-50" : "hover:bg-brand-pale hover:border-brand-primary text-gray-600"}`}>
      {uploading ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
      {label}
      <input type="file" accept="image/*" className="hidden" onChange={handleChange} disabled={uploading} />
    </label>
  );
}

// ── Shared: Video Upload Button ──────────────────────────
function VideoUploadBtn({ onUploaded, label = "Upload Video (Max 100MB)" }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) {
      alert(`Video file size cannot exceed 100MB. Selected file is ${(file.size / (1024 * 1024)).toFixed(1)}MB.`);
      if (e.target) e.target.value = "";
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const { data } = await uploadAPI.video(file, "videos", (percent) => {
        setProgress(percent);
      });
      onUploaded(data.url);
    } catch (err) {
      console.error("Video upload error:", err);
      let msg = "Video upload failed";
      if (err.code === "ECONNABORTED" || err.message?.includes("timeout")) {
        msg = "Upload timed out. The file took too long to transfer. Please check your internet connection.";
      } else if (err.response?.data?.error) {
        msg = err.response.data.error;
      } else if (err.message) {
        msg = err.message;
      }
      alert(`Upload error: ${msg}`);
    } finally {
      setUploading(false);
      setProgress(0);
      if (e.target) e.target.value = "";
    }
  };

  return (
    <label className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 text-xs font-semibold cursor-pointer transition-colors ${uploading ? "opacity-75 pointer-events-none bg-brand-pale text-brand-primary border-brand-primary" : "hover:bg-brand-pale hover:border-brand-primary text-gray-600"}`}>
      {uploading ? <Loader className="w-3.5 h-3.5 animate-spin" /> : "🎥"}
      {uploading ? `Uploading ${progress > 0 ? `${progress}%` : "..."}` : label}
      <input type="file" accept="video/mp4,video/webm,video/ogg,video/quicktime,video/*" className="hidden" onChange={handleChange} disabled={uploading} />
    </label>
  );
}

// ── Modal Wrapper ────────────────────────────────────────
function Modal({ title, onClose, children, footer }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4 py-8 overflow-y-auto">
      <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full my-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white rounded-t-3xl">
          <h3 className="font-heading font-bold text-gray-900 text-lg">{title}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5">{children}</div>
        {footer && <div className="p-5 pt-0 border-t border-gray-100 flex gap-3">{footer}</div>}
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ADMIN PRODUCTS
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const emptyProduct = { name:"", category:"", shortDesc:"", description:"", price:"", imageUrl:"", images:"", specifications:"", featured:false, isActive:true };

export function AdminProducts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const r = await productsAPI.getAll(); setItems(r.data.products || []); } catch {}
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const toSlug = s => s.toLowerCase().replace(/[^a-z0-9]+/g,"-").replace(/(^-|-$)/g,"");
  const f = (k) => (v) => setForm(p => ({...p,[k]:v}));

  const openAdd = () => { setEditItem(null); setForm(emptyProduct); setShowForm(true); };
  const openEdit = (item) => { setEditItem(item); setForm({...emptyProduct,...item}); setShowForm(true); };

  const handleSave = async () => {
    if (!form.name.trim()) { alert("Product name required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, slug: toSlug(form.name) };
      if (editItem) await productsAPI.update(editItem.id, payload);
      else await productsAPI.create(payload);
      setShowForm(false); fetch();
    } catch (err) { alert(err.response?.data?.error || "Save failed"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await productsAPI.delete(id); fetch(); } catch {}
  };

  const filtered = items.filter(i => !search || i.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h2 className="font-heading font-bold text-gray-900 text-xl">Products</h2><p className="text-gray-400 text-sm">{items.length} products</p></div>
        <div className="flex gap-2">
          <button onClick={fetch} className="w-9 h-9 flex items-center justify-center bg-white border border-gray-200 rounded-xl hover:bg-gray-50 shadow-soft"><RefreshCw className="w-4 h-4 text-gray-500" /></button>
          <button onClick={openAdd} className="btn-primary text-xs px-4 py-2.5"><Plus className="w-4 h-4" /> Add Product</button>
        </div>
      </div>

      <div className="relative max-w-xs">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input className="input-field pl-9 py-2 text-sm" placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {loading ? <div className="flex items-center justify-center py-16"><Loader className="w-6 h-6 text-brand-primary animate-spin mr-2" /><span className="text-gray-400 text-sm">Loading...</span></div>
      : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(item => (
            <div key={item.id} className="bg-white rounded-2xl shadow-card hover:shadow-hover transition-all duration-300 overflow-hidden">
              <div className="h-36 bg-brand-pale overflow-hidden">
                {item.imageUrl ? <img src={getImageUrl(item.imageUrl)} alt={item.name} className="w-full h-full object-cover" onError={e=>e.target.style.display="none"} /> : <div className="w-full h-full flex items-center justify-center"><Package className="w-10 h-10 text-brand-muted" /></div>}
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h3 className="font-heading font-bold text-gray-900 text-sm">{item.name}</h3>
                  {item.featured && <span className="badge badge-gold text-xs">★ Featured</span>}
                </div>
                {item.category && <span className="badge-brand text-xs mb-2 inline-block">{item.category}</span>}
                <div className="flex gap-2 mt-3">
                  <button onClick={() => openEdit(item)} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-brand-pale text-brand-primary text-xs font-semibold hover:bg-brand-muted transition-colors"><Edit2 className="w-3.5 h-3.5" /> Edit</button>
                  <button onClick={() => handleDelete(item.id)} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-red-50 text-red-500 text-xs font-semibold hover:bg-red-100 transition-colors"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                </div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className="col-span-3 text-center py-16 text-gray-400"><Package className="w-12 h-12 mx-auto mb-3 text-gray-200" />No products found</div>}
        </div>
      )}

      {showForm && (
        <Modal title={editItem ? "Edit Product" : "Add Product"} onClose={() => setShowForm(false)}
          footer={<>
            <button onClick={() => setShowForm(false)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="flex-1 btn-primary justify-center py-2.5 text-sm">
              {saving ? <><Loader className="w-4 h-4 animate-spin" />Saving...</> : <><Save className="w-4 h-4" />{editItem ? "Update" : "Add"} Product</>}
            </button>
          </>}>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="block text-xs font-semibold text-gray-600 mb-1.5">Product Name *</label><input className="input-field" value={form.name} onChange={e => f("name")(e.target.value)} placeholder="e.g. Premium Basmati Rice" /></div>
              <div><label className="block text-xs font-semibold text-gray-600 mb-1.5">Category</label><input className="input-field" value={form.category} onChange={e => f("category")(e.target.value)} placeholder="e.g. Grains & Rice" /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="block text-xs font-semibold text-gray-600 mb-1.5">Price (Optional)</label><input className="input-field" value={form.price} onChange={e => f("price")(e.target.value)} placeholder="e.g. $1.50/kg" /></div>
              <div className="flex items-center gap-2 pt-6"><input type="checkbox" id="featured" checked={form.featured} onChange={e => f("featured")(e.target.checked)} className="w-4 h-4 accent-brand-primary" /><label htmlFor="featured" className="text-sm font-medium text-gray-700">Mark as Featured</label></div>
            </div>
            <div><label className="block text-xs font-semibold text-gray-600 mb-1.5">Short Description</label><textarea className="input-field resize-none" rows={2} value={form.shortDesc} onChange={e => f("shortDesc")(e.target.value)} placeholder="Brief description for listings..." /></div>
            <div><label className="block text-xs font-semibold text-gray-600 mb-1.5">Full Description</label><textarea className="input-field resize-none" rows={4} value={form.description} onChange={e => f("description")(e.target.value)} placeholder="Full product description (HTML supported)..." /></div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Main Image</label>
              <div className="flex gap-2">
                <input className="input-field text-sm flex-1" value={form.imageUrl} onChange={e => f("imageUrl")(e.target.value)} placeholder="Image URL..." />
                <ImageUploadBtn onUploaded={url => f("imageUrl")(url)} type="products" />
              </div>
              {form.imageUrl && <img src={getImageUrl(form.imageUrl)} alt="" className="mt-2 h-24 w-auto rounded-xl object-cover" onError={e=>e.target.style.display="none"} />}
            </div>
            <div><label className="block text-xs font-semibold text-gray-600 mb-1.5">Specifications (JSON)</label><textarea className="input-field resize-none font-mono text-xs" rows={3} value={form.specifications} onChange={e => f("specifications")(e.target.value)} placeholder='{"Origin":"India","Grade":"A","Moisture":"≤12%"}' /></div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ADMIN SERVICES
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const emptyService = { title:"", slug:"", excerpt:"", description:"", imageUrl:"", isActive:true };

export function AdminServices() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyService);
  const [saving, setSaving] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const r = await servicesAPI.getAll(); setItems(r.data.services || []); } catch {}
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const f = (k) => (v) => setForm(p => ({...p,[k]:v}));
  const toSlug = s => s.toLowerCase().replace(/[^a-z0-9]+/g,"-").replace(/(^-|-$)/g,"");
  const openAdd = () => { setEditItem(null); setForm(emptyService); setShowForm(true); };
  const openEdit = (item) => { setEditItem(item); setForm({...emptyService,...item}); setShowForm(true); };

  const handleSave = async () => {
    if (!form.title.trim()) { alert("Service title required"); return; }
    setSaving(true);
    try {
      const payload = { 
        ...form, 
        slug: form.slug ? toSlug(form.slug) : toSlug(form.title),
        description: form.description ? "base64:" + btoa(unescape(encodeURIComponent(form.description))) : ""
      };
      if (editItem) await servicesAPI.update(editItem.id, payload);
      else await servicesAPI.create(payload);
      setShowForm(false); fetch();
    } catch (err) { alert(err.response?.data?.error || "Save failed"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    try { await servicesAPI.delete(id); fetch(); } catch {}
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h2 className="font-heading font-bold text-gray-900 text-xl">Services</h2><p className="text-gray-400 text-sm">{items.length} services</p></div>
        <div className="flex gap-2">
          <button onClick={fetch} className="w-9 h-9 flex items-center justify-center bg-white border border-gray-200 rounded-xl hover:bg-gray-50 shadow-soft"><RefreshCw className="w-4 h-4 text-gray-500" /></button>
          <button onClick={openAdd} className="btn-primary text-xs px-4 py-2.5"><Plus className="w-4 h-4" /> Add Service</button>
        </div>
      </div>

      {loading ? <div className="flex items-center justify-center py-16"><Loader className="w-6 h-6 text-brand-primary animate-spin" /></div>
      : (
        <div className="grid md:grid-cols-2 gap-5">
          {items.map(item => (
            <div key={item.id} className="bg-white rounded-2xl shadow-card overflow-hidden flex">
              <div className="w-24 bg-brand-pale flex-shrink-0 flex items-center justify-center">
                {item.imageUrl ? <img src={getImageUrl(item.imageUrl)} alt={item.title} className="w-full h-full object-cover" onError={e=>e.target.style.display="none"} /> : <Wrench className="w-8 h-8 text-brand-muted" />}
              </div>
              <div className="p-4 flex-1">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h3 className="font-heading font-bold text-gray-900 text-sm">{item.title}</h3>
                  <a href={`/services/${item.slug}`} target="_blank" rel="noopener noreferrer" className="text-[11px] text-brand-primary hover:underline font-mono">
                    /services/{item.slug}
                  </a>
                </div>
                <p className="text-gray-500 text-xs line-clamp-2 mb-3">{item.excerpt}</p>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(item)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-brand-pale text-brand-primary text-xs font-semibold hover:bg-brand-muted transition-colors"><Edit2 className="w-3 h-3" /> Edit</button>
                  <button onClick={() => handleDelete(item.id)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-50 text-red-500 text-xs font-semibold hover:bg-red-100 transition-colors"><Trash2 className="w-3 h-3" /> Delete</button>
                </div>
              </div>
            </div>
          ))}
          {items.length === 0 && <div className="col-span-2 text-center py-16 text-gray-400"><Wrench className="w-12 h-12 mx-auto mb-3 text-gray-200" />No services yet</div>}
        </div>
      )}

      {showForm && (
        <Modal title={editItem ? "Edit Service" : "Add Service"} onClose={() => setShowForm(false)}
          footer={<>
            <button onClick={() => setShowForm(false)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="flex-1 btn-primary justify-center py-2.5 text-sm">{saving ? <><Loader className="w-4 h-4 animate-spin" />Saving...</> : <><Save className="w-4 h-4" />{editItem?"Update":"Add"} Service</>}</button>
          </>}>
          <div className="space-y-4">
            <div><label className="block text-xs font-semibold text-gray-600 mb-1.5">Service Title *</label><input className="input-field" value={form.title} onChange={e=>f("title")(e.target.value)} placeholder="e.g. Global Sourcing" /></div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">SEO Friendly Slug (URL)</label>
              <div className="flex items-center">
                <span className="bg-gray-100 border border-r-0 border-gray-200 text-gray-400 text-xs px-3 py-2.5 rounded-l-xl">/services/</span>
                <input className="input-field rounded-l-none text-xs font-mono" value={form.slug || ""} onChange={e=>f("slug")(toSlug(e.target.value))} placeholder="e.g. global-sourcing" />
              </div>
              <p className="text-[10px] text-gray-400 mt-1">Leave empty or modify to create a clean, meaningful URL without random characters. Old URLs will 301 redirect automatically.</p>
            </div>
            <div><label className="block text-xs font-semibold text-gray-600 mb-1.5">Short Description</label><textarea className="input-field resize-none" rows={2} value={form.excerpt} onChange={e=>f("excerpt")(e.target.value)} placeholder="Brief summary..." /></div>
            <div><label className="block text-xs font-semibold text-gray-600 mb-1.5">Full Description</label><WysiwygEditor value={form.description} onChange={val=>f("description")(val)} /></div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Image</label>
              <div className="flex gap-2">
                <input className="input-field text-sm flex-1" value={form.imageUrl} onChange={e=>f("imageUrl")(e.target.value)} placeholder="Image URL..." />
                <ImageUploadBtn onUploaded={url=>f("imageUrl")(url)} type="services" />
              </div>
              {form.imageUrl && <img src={getImageUrl(form.imageUrl)} alt="" className="mt-2 h-20 w-auto rounded-xl object-cover" onError={e=>e.target.style.display="none"} />}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ADMIN MESSAGES (Contact Submissions)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const msgStatusColors = { NEW:"bg-orange-50 text-orange-600", READ:"bg-blue-50 text-blue-600", RESOLVED:"bg-green-50 text-green-700" };

export function AdminMessages() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const r = await contactAPI.getAll(); setItems(r.data.submissions || []); } catch {}
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const updateStatus = async (id, status) => {
    try { await contactAPI.updateStatus(id, status); setItems(p => p.map(m => m.id===id?{...m,status}:m)); if(selected?.id===id) setSelected(prev=>({...prev,status})); } catch {}
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try { await contactAPI.delete(id); setItems(p=>p.filter(m=>m.id!==id)); if(selected?.id===id) setSelected(null); } catch {}
  };

  const exportCSV = () => {
    const h=["Name","Email","Phone","Subject","Message","Status","Date"];
    const r=items.map(m=>[m.name,m.email,m.phone||"",m.subject||"",m.message||"",m.status,new Date(m.createdAt).toLocaleDateString()]);
    const csv=[h,...r].map(row=>row.map(v=>`"${v}"`).join(",")).join("\n");
    const a=document.createElement("a"); a.href=URL.createObjectURL(new Blob([csv],{type:"text/csv"})); a.download=`messages-${new Date().toISOString().slice(0,10)}.csv`; a.click();
  };

  const filtered = items.filter(m => !search || m.name.toLowerCase().includes(search.toLowerCase()) || m.email.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h2 className="font-heading font-bold text-gray-900 text-xl">Contact Messages</h2><p className="text-gray-400 text-sm">{items.length} messages · {items.filter(m=>m.status==="NEW").length} new</p></div>
        <div className="flex gap-2">
          <button onClick={fetch} className="w-9 h-9 flex items-center justify-center bg-white border border-gray-200 rounded-xl hover:bg-gray-50 shadow-soft"><RefreshCw className="w-4 h-4 text-gray-500" /></button>
          <button onClick={exportCSV} className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 shadow-soft"><Download className="w-4 h-4" /> Export</button>
        </div>
      </div>
      <div className="relative max-w-xs"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" /><input className="input-field pl-9 py-2 text-sm" placeholder="Search messages..." value={search} onChange={e=>setSearch(e.target.value)} /></div>

      <div className="bg-white rounded-2xl shadow-card overflow-hidden">
        {loading ? <div className="flex items-center justify-center py-16"><Loader className="w-6 h-6 text-brand-primary animate-spin mr-2" /><span className="text-gray-400 text-sm">Loading...</span></div>
        : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="bg-gray-50 border-b border-gray-100">{["Name","Subject","Date","Status","Actions"].map(h=><th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.length===0 ? <tr><td colSpan={5} className="px-4 py-12 text-center text-gray-400 text-sm">No messages found</td></tr>
                : filtered.map(msg => (
                  <tr key={msg.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 bg-brand-pale rounded-full flex items-center justify-center text-brand-primary font-bold text-xs flex-shrink-0">{msg.name?.charAt(0)}</div>
                        <div><div className="font-medium text-gray-900 text-sm">{msg.name}</div><div className="text-xs text-gray-400">{msg.email}</div></div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-gray-600 max-w-xs truncate">{msg.subject || msg.message?.slice(0,40)}</td>
                    <td className="px-4 py-3.5 text-xs text-gray-400">{new Date(msg.createdAt).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}</td>
                    <td className="px-4 py-3.5"><select value={msg.status} onChange={e=>updateStatus(msg.id,e.target.value)} className={`text-xs px-2 py-1 rounded-lg font-semibold cursor-pointer border-none outline-none ${msgStatusColors[msg.status]||""}`}><option value="NEW">New</option><option value="READ">Read</option><option value="RESOLVED">Resolved</option></select></td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-1.5">
                        <button onClick={()=>setSelected(msg)} className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center hover:bg-blue-50 hover:text-blue-600 transition-colors text-gray-500" title="View"><Eye className="w-3.5 h-3.5" /></button>
                        <a href={`mailto:${msg.email}?subject=Re: ${msg.subject||"Your Enquiry"}`} className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center hover:bg-green-50 hover:text-green-600 transition-colors text-gray-500" title="Reply"><Send className="w-3.5 h-3.5" /></a>
                        <button onClick={()=>handleDelete(msg.id)} className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center hover:bg-red-50 hover:text-red-500 transition-colors text-gray-500" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <Modal title="Message Details" onClose={() => setSelected(null)}
          footer={<>
            <button onClick={()=>setSelected(null)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm">Close</button>
            <a href={`mailto:${selected.email}?subject=Re: ${selected.subject||"Your Enquiry"}`} className="flex-1 btn-primary justify-center py-2.5 text-sm"><Send className="w-4 h-4" /> Reply via Email</a>
          </>}>
          <div className="space-y-3">
            {[["Name",selected.name],["Email",selected.email],["Phone",selected.phone||"—"],["Subject",selected.subject||"—"],["Date",new Date(selected.createdAt).toLocaleString("en-US")]].map(([k,v])=>(
              <div key={k} className="flex gap-3 py-2 border-b border-gray-50"><span className="text-xs text-gray-400 w-20 flex-shrink-0 pt-0.5 font-medium">{k}</span><span className="text-sm text-gray-800 font-medium">{v}</span></div>
            ))}
            <div><span className="text-xs text-gray-400 block mb-1 font-medium">Message</span><div className="bg-gray-50 rounded-xl p-3 text-sm text-gray-700 leading-relaxed">{selected.message}</div></div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── JSON Repeater Components ───────────────────────────

function parseJsonArray(str, defaultVal = []) {
  if (!str) return defaultVal;
  let clean = str.replace(/<[^>]*>/g, "");
  clean = clean
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ');
  try {
    const val = JSON.parse(clean);
    return Array.isArray(val) ? val : defaultVal;
  } catch {
    return defaultVal;
  }
}

function NavigationMenuRepeater({ value, onChange }) {
  const [items, setItems] = useState(() => parseJsonArray(value, [
    { label: "Home", path: "/" },
    { label: "About", path: "/about", hasDropdown: true, submenu: [
      { label: "About Us", path: "/about", icon: "Info" },
      { label: "Our Services", path: "/services", icon: "Wrench" }
    ] }
  ]));

  useEffect(() => {
    const parsed = parseJsonArray(value);
    if (JSON.stringify(parsed) !== JSON.stringify(items)) {
      setItems(parsed);
    }
  }, [value]);

  const update = (updated) => {
    setItems(updated);
    onChange(JSON.stringify(updated));
  };

  const setItemField = (idx, field, val) => {
    const next = [...items];
    next[idx] = { ...next[idx], [field]: val };
    if (field === "hasDropdown") {
      if (val && !next[idx].submenu) {
        next[idx].submenu = [];
      } else if (!val) {
        delete next[idx].submenu;
      }
    }
    update(next);
  };

  const move = (idx, dir) => {
    const next = [...items];
    const targetIdx = idx + dir;
    if (targetIdx < 0 || targetIdx >= next.length) return;
    const temp = next[idx];
    next[idx] = next[targetIdx];
    next[targetIdx] = temp;
    update(next);
  };

  const remove = (idx) => {
    update(items.filter((_, i) => i !== idx));
  };

  const add = () => {
    update([...items, { label: "New Item", path: "/" }]);
  };

  const addSub = (idx) => {
    const next = [...items];
    const sub = next[idx].submenu || [];
    next[idx] = { ...next[idx], submenu: [...sub, { label: "Sub Item", path: "/", icon: "ChevronRight" }] };
    update(next);
  };

  const removeSub = (idx, subIdx) => {
    const next = [...items];
    next[idx] = { ...next[idx], submenu: (next[idx].submenu || []).filter((_, i) => i !== subIdx) };
    update(next);
  };

  const setSubField = (idx, subIdx, field, val) => {
    const next = [...items];
    const sub = [...(next[idx].submenu || [])];
    sub[subIdx] = { ...sub[subIdx], [field]: val };
    next[idx] = { ...next[idx], submenu: sub };
    update(next);
  };

  const moveSub = (idx, subIdx, dir) => {
    const next = [...items];
    const sub = [...(next[idx].submenu || [])];
    const targetIdx = subIdx + dir;
    if (targetIdx < 0 || targetIdx >= sub.length) return;
    const temp = sub[subIdx];
    sub[subIdx] = sub[targetIdx];
    sub[targetIdx] = temp;
    next[idx] = { ...next[idx], submenu: sub };
    update(next);
  };

  return (
    <div className="space-y-4 border border-gray-150 rounded-2xl p-4 bg-gray-50/50">
      <div className="space-y-3">
        {items.map((item, idx) => (
          <div key={idx} className="bg-white border border-gray-100 rounded-xl p-3.5 shadow-sm space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-gray-400">#{idx + 1}</span>
              <input
                type="text"
                placeholder="Label"
                className="input-field text-xs py-1.5 flex-1"
                value={item.label || ""}
                onChange={(e) => setItemField(idx, "label", e.target.value)}
              />
              <input
                type="text"
                placeholder="Link Path (e.g. /about)"
                className="input-field text-xs py-1.5 flex-1"
                value={item.path || ""}
                onChange={(e) => setItemField(idx, "path", e.target.value)}
              />
              <label className="flex items-center gap-1.5 text-xs text-gray-600 font-medium whitespace-nowrap cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!item.hasDropdown}
                  onChange={(e) => setItemField(idx, "hasDropdown", e.target.checked)}
                  className="w-3.5 h-3.5 accent-brand-primary"
                />
                Dropdown
              </label>
              <div className="flex gap-0.5">
                <button type="button" disabled={idx === 0} onClick={() => move(idx, -1)} className="w-6 h-6 rounded bg-gray-50 hover:bg-gray-100 text-gray-500 disabled:opacity-30 flex items-center justify-center"><ArrowUp className="w-3.5 h-3.5" /></button>
                <button type="button" disabled={idx === items.length - 1} onClick={() => move(idx, 1)} className="w-6 h-6 rounded bg-gray-50 hover:bg-gray-100 text-gray-500 disabled:opacity-30 flex items-center justify-center"><ArrowDown className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => remove(idx)} className="w-6 h-6 rounded bg-red-50 hover:bg-red-100 text-red-500 flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>

            {item.hasDropdown && (
              <div className="pl-6 border-l-2 border-brand-pale space-y-2.5">
                <span className="text-[10px] uppercase font-bold text-brand-primary/60 tracking-wider block">Submenu Items</span>
                {(item.submenu || []).map((sub, sIdx) => (
                  <div key={sIdx} className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Sub Label"
                      className="input-field text-xs py-1.5 flex-1"
                      value={sub.label || ""}
                      onChange={(e) => setSubField(idx, sIdx, "label", e.target.value)}
                    />
                    <input
                      type="text"
                      placeholder="Sub Path"
                      className="input-field text-xs py-1.5 flex-1"
                      value={sub.path || ""}
                      onChange={(e) => setSubField(idx, sIdx, "path", e.target.value)}
                    />
                    <input
                      type="text"
                      placeholder="Icon Component"
                      className="input-field text-xs py-1.5 w-32"
                      value={sub.icon || ""}
                      onChange={(e) => setSubField(idx, sIdx, "icon", e.target.value)}
                    />
                    <div className="flex gap-0.5">
                      <button type="button" disabled={sIdx === 0} onClick={() => moveSub(idx, sIdx, -1)} className="w-6 h-6 rounded bg-gray-50 hover:bg-gray-100 text-gray-500 disabled:opacity-30 flex items-center justify-center"><ArrowUp className="w-3.5 h-3.5" /></button>
                      <button type="button" disabled={sIdx === (item.submenu || []).length - 1} onClick={() => moveSub(idx, sIdx, 1)} className="w-6 h-6 rounded bg-gray-50 hover:bg-gray-100 text-gray-500 disabled:opacity-30 flex items-center justify-center"><ArrowDown className="w-3.5 h-3.5" /></button>
                      <button type="button" onClick={() => removeSub(idx, sIdx)} className="w-6 h-6 rounded bg-red-50 hover:bg-red-100 text-red-500 flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addSub(idx)}
                  className="flex items-center gap-1 text-[11px] font-semibold text-brand-primary hover:underline"
                >
                  <Plus className="w-3 h-3" /> Add Submenu Link
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={add}
        className="w-full flex items-center justify-center gap-1.5 py-2 border border-dashed border-gray-200 hover:border-brand-primary rounded-xl text-xs font-semibold text-gray-600 hover:text-brand-primary bg-white hover:bg-brand-pale/10 transition-all"
      >
        <Plus className="w-4 h-4" /> Add Menu Item
      </button>
    </div>
  );
}

function LinkListRepeater({ value, onChange }) {
  const [items, setItems] = useState(() => parseJsonArray(value, []));

  useEffect(() => {
    const parsed = parseJsonArray(value);
    if (JSON.stringify(parsed) !== JSON.stringify(items)) setItems(parsed);
  }, [value]);

  const update = (updated) => {
    setItems(updated);
    onChange(JSON.stringify(updated));
  };

  const setItemField = (idx, field, val) => {
    const next = [...items];
    next[idx] = { ...next[idx], [field]: val };
    update(next);
  };

  const move = (idx, dir) => {
    const next = [...items];
    const targetIdx = idx + dir;
    if (targetIdx < 0 || targetIdx >= next.length) return;
    const temp = next[idx];
    next[idx] = next[targetIdx];
    next[targetIdx] = temp;
    update(next);
  };

  const remove = (idx) => update(items.filter((_, i) => i !== idx));
  const add = () => update([...items, { label: "New Link", path: "/" }]);

  return (
    <div className="space-y-3 border border-gray-100 rounded-2xl p-4 bg-gray-50/50">
      <div className="space-y-2">
        {items.map((item, idx) => (
          <div key={idx} className="flex items-center gap-2 bg-white p-2 rounded-xl border border-gray-100 shadow-sm">
            <span className="text-xs font-bold text-gray-400 px-1">#{idx + 1}</span>
            <input
              type="text"
              placeholder="Link Label"
              className="input-field text-xs py-1.5 flex-1"
              value={item.label || ""}
              onChange={(e) => setItemField(idx, "label", e.target.value)}
            />
            <input
              type="text"
              placeholder="Path (e.g. /about)"
              className="input-field text-xs py-1.5 flex-1"
              value={item.path || ""}
              onChange={(e) => setItemField(idx, "path", e.target.value)}
            />
            <div className="flex gap-0.5">
              <button type="button" disabled={idx === 0} onClick={() => move(idx, -1)} className="w-6 h-6 rounded bg-gray-50 hover:bg-gray-100 text-gray-500 disabled:opacity-30 flex items-center justify-center"><ArrowUp className="w-3.5 h-3.5" /></button>
              <button type="button" disabled={idx === items.length - 1} onClick={() => move(idx, 1)} className="w-6 h-6 rounded bg-gray-50 hover:bg-gray-100 text-gray-500 disabled:opacity-30 flex items-center justify-center"><ArrowDown className="w-3.5 h-3.5" /></button>
              <button type="button" onClick={() => remove(idx)} className="w-6 h-6 rounded bg-red-50 hover:bg-red-100 text-red-500 flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-xs text-gray-400 text-center py-2">No links defined.</p>}
      </div>
      <button
        type="button"
        onClick={add}
        className="w-full flex items-center justify-center gap-1 py-1.5 border border-dashed border-gray-200 hover:border-brand-primary rounded-xl text-xs font-semibold text-gray-600 hover:text-brand-primary bg-white transition-all"
      >
        <Plus className="w-3.5 h-3.5" /> Add Link Item
      </button>
    </div>
  );
}

function StatsRepeater({ value, onChange }) {
  const [items, setItems] = useState(() => parseJsonArray(value, []));

  useEffect(() => {
    const parsed = parseJsonArray(value);
    if (JSON.stringify(parsed) !== JSON.stringify(items)) setItems(parsed);
  }, [value]);

  const update = (updated) => {
    setItems(updated);
    onChange(JSON.stringify(updated));
  };

  const setItemField = (idx, field, val) => {
    const next = [...items];
    next[idx] = { ...next[idx], [field]: field === "value" ? Number(val) || 0 : val };
    update(next);
  };

  const move = (idx, dir) => {
    const next = [...items];
    const targetIdx = idx + dir;
    if (targetIdx < 0 || targetIdx >= next.length) return;
    const temp = next[idx];
    next[idx] = next[targetIdx];
    next[targetIdx] = temp;
    update(next);
  };

  const remove = (idx) => update(items.filter((_, i) => i !== idx));
  const add = () => update([...items, { value: 0, suffix: "+", label: "New Stat", icon: "Globe" }]);

  const iconsList = ["Globe", "Package", "Users", "TrendingUp", "Shield", "Award", "Truck", "Leaf", "CheckCircle", "Star"];

  return (
    <div className="space-y-3 border border-gray-150 rounded-2xl p-4 bg-gray-50/50">
      <div className="space-y-3">
        {items.map((item, idx) => (
          <div key={idx} className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-gray-400">Stat Card #{idx + 1}</span>
              <div className="flex gap-0.5">
                <button type="button" disabled={idx === 0} onClick={() => move(idx, -1)} className="w-6 h-6 rounded bg-gray-50 hover:bg-gray-100 text-gray-500 disabled:opacity-30 flex items-center justify-center"><ArrowUp className="w-3.5 h-3.5" /></button>
                <button type="button" disabled={idx === items.length - 1} onClick={() => move(idx, 1)} className="w-6 h-6 rounded bg-gray-50 hover:bg-gray-100 text-gray-500 disabled:opacity-30 flex items-center justify-center"><ArrowDown className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => remove(idx)} className="w-6 h-6 rounded bg-red-50 hover:bg-red-100 text-red-500 flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            <div className="grid sm:grid-cols-4 gap-2">
              <div>
                <label className="block text-[10px] font-semibold text-gray-400 mb-1">Number Value</label>
                <input
                  type="number"
                  placeholder="e.g. 50"
                  className="input-field text-xs py-1.5"
                  value={item.value || 0}
                  onChange={(e) => setItemField(idx, "value", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-400 mb-1">Suffix</label>
                <input
                  type="text"
                  placeholder="e.g. +"
                  className="input-field text-xs py-1.5"
                  value={item.suffix || ""}
                  onChange={(e) => setItemField(idx, "suffix", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-400 mb-1">Label</label>
                <input
                  type="text"
                  placeholder="e.g. Countries Served"
                  className="input-field text-xs py-1.5"
                  value={item.label || ""}
                  onChange={(e) => setItemField(idx, "label", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-400 mb-1">Icon</label>
                <select
                  className="input-field text-xs py-1.5"
                  value={item.icon || "Globe"}
                  onChange={(e) => setItemField(idx, "icon", e.target.value)}
                >
                  {iconsList.map(ic => <option key={ic} value={ic}>{ic}</option>)}
                </select>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-xs text-gray-400 text-center py-2">No stats counters defined.</p>}
      </div>
      <button
        type="button"
        onClick={add}
        className="w-full flex items-center justify-center gap-1.5 py-2 border border-dashed border-gray-200 hover:border-brand-primary rounded-xl text-xs font-semibold text-gray-600 hover:text-brand-primary bg-white transition-all"
      >
        <Plus className="w-4 h-4" /> Add Stat Card
      </button>
    </div>
  );
}

function CardListRepeater({ value, onChange }) {
  const [items, setItems] = useState(() => parseJsonArray(value, []));

  useEffect(() => {
    const parsed = parseJsonArray(value);
    if (JSON.stringify(parsed) !== JSON.stringify(items)) setItems(parsed);
  }, [value]);

  const update = (updated) => {
    setItems(updated);
    onChange(JSON.stringify(updated));
  };

  const setItemField = (idx, field, val) => {
    const next = [...items];
    next[idx] = { ...next[idx], [field]: val };
    update(next);
  };

  const move = (idx, dir) => {
    const next = [...items];
    const targetIdx = idx + dir;
    if (targetIdx < 0 || targetIdx >= next.length) return;
    const temp = next[idx];
    next[idx] = next[targetIdx];
    next[targetIdx] = temp;
    update(next);
  };

  const remove = (idx) => update(items.filter((_, i) => i !== idx));
  const add = () => update([...items, { icon: "Shield", title: "New Card Item", desc: "", color: "brand" }]);

  const iconsList = ["Shield", "Globe", "Truck", "Award", "Leaf", "CheckCircle", "Users", "TrendingUp", "Star", "Target", "Eye", "Wrench", "Package"];

  return (
    <div className="space-y-3 border border-gray-150 rounded-2xl p-4 bg-gray-50/50">
      <div className="space-y-3">
        {items.map((item, idx) => (
          <div key={idx} className="bg-white border border-gray-100 rounded-xl p-3.5 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-gray-400 font-heading">Card Item #{idx + 1}</span>
              <div className="flex gap-0.5">
                <button type="button" disabled={idx === 0} onClick={() => move(idx, -1)} className="w-6 h-6 rounded bg-gray-50 hover:bg-gray-100 text-gray-500 disabled:opacity-30 flex items-center justify-center"><ArrowUp className="w-3.5 h-3.5" /></button>
                <button type="button" disabled={idx === items.length - 1} onClick={() => move(idx, 1)} className="w-6 h-6 rounded bg-gray-50 hover:bg-gray-100 text-gray-500 disabled:opacity-30 flex items-center justify-center"><ArrowDown className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => remove(idx)} className="w-6 h-6 rounded bg-red-50 hover:bg-red-100 text-red-500 flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-2">
              <div>
                <label className="block text-[10px] font-semibold text-gray-400 mb-1">Title</label>
                <input
                  type="text"
                  placeholder="Card Title"
                  className="input-field text-xs py-1.5"
                  value={item.title || ""}
                  onChange={(e) => setItemField(idx, "title", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-400 mb-1">Icon</label>
                <select
                  className="input-field text-xs py-1.5"
                  value={item.icon || "Shield"}
                  onChange={(e) => setItemField(idx, "icon", e.target.value)}
                >
                  {iconsList.map(ic => <option key={ic} value={ic}>{ic}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-400 mb-1">Highlight Color</label>
                <select
                  className="input-field text-xs py-1.5"
                  value={item.color || "brand"}
                  onChange={(e) => setItemField(idx, "color", e.target.value)}
                >
                  <option value="brand">Brand Teal</option>
                  <option value="gold">Accent Gold</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-gray-400 mb-1">Description</label>
              <textarea
                placeholder="Write card description..."
                className="input-field text-xs py-1.5 resize-none"
                rows={2}
                value={item.desc || ""}
                onChange={(e) => setItemField(idx, "desc", e.target.value)}
              />
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-xs text-gray-400 text-center py-2">No cards defined.</p>}
      </div>
      <button
        type="button"
        onClick={add}
        className="w-full flex items-center justify-center gap-1.5 py-2 border border-dashed border-gray-200 hover:border-brand-primary rounded-xl text-xs font-semibold text-gray-600 hover:text-brand-primary bg-white transition-all"
      >
        <Plus className="w-4 h-4" /> Add Card Item
      </button>
    </div>
  );
}

function CategoryCardsRepeater({ value, onChange }) {
  const [items, setItems] = useState(() => parseJsonArray(value, []));

  useEffect(() => {
    const parsed = parseJsonArray(value);
    if (JSON.stringify(parsed) !== JSON.stringify(items)) setItems(parsed);
  }, [value]);

  const update = (updated) => {
    setItems(updated);
    onChange(JSON.stringify(updated));
  };

  const setItemField = (idx, field, val) => {
    const next = [...items];
    next[idx] = { ...next[idx], [field]: val };
    update(next);
  };

  const move = (idx, dir) => {
    const next = [...items];
    const targetIdx = idx + dir;
    if (targetIdx < 0 || targetIdx >= next.length) return;
    const temp = next[idx];
    next[idx] = next[targetIdx];
    next[targetIdx] = temp;
    update(next);
  };

  const remove = (idx) => update(items.filter((_, i) => i !== idx));
  const add = () => update([...items, { label: "New Category", bg: "from-green-50 to-green-100", icon: "Leaf" }]);

  const iconsList = ["Leaf", "Package", "Shield", "Globe", "Award", "Truck", "Users", "TrendingUp", "Star", "Target", "Eye", "Wrench"];
  const gradientsList = [
    { label: "Fresh Green", value: "from-green-50 to-green-100" },
    { label: "Grain Yellow", value: "from-yellow-50 to-yellow-100" },
    { label: "Seafood Blue", value: "from-blue-50 to-blue-100" },
    { label: "Beverage Purple", value: "from-purple-50 to-purple-100" },
    { label: "Spices Orange", value: "from-orange-50 to-orange-100" },
    { label: "Teal Soft", value: "from-teal-50 to-teal-100" },
    { label: "Red Soft", value: "from-red-50 to-red-100" },
  ];

  return (
    <div className="space-y-3 border border-gray-150 rounded-2xl p-4 bg-gray-50/50">
      <div className="space-y-3">
        {items.map((item, idx) => (
          <div key={idx} className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-gray-400">Category Card #{idx + 1}</span>
              <div className="flex gap-0.5">
                <button type="button" disabled={idx === 0} onClick={() => move(idx, -1)} className="w-6 h-6 rounded bg-gray-50 hover:bg-gray-100 text-gray-500 disabled:opacity-30 flex items-center justify-center"><ArrowUp className="w-3.5 h-3.5" /></button>
                <button type="button" disabled={idx === items.length - 1} onClick={() => move(idx, 1)} className="w-6 h-6 rounded bg-gray-50 hover:bg-gray-100 text-gray-500 disabled:opacity-30 flex items-center justify-center"><ArrowDown className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => remove(idx)} className="w-6 h-6 rounded bg-red-50 hover:bg-red-100 text-red-500 flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-2">
              <div>
                <label className="block text-[10px] font-semibold text-gray-400 mb-1">Label</label>
                <input
                  type="text"
                  placeholder="e.g. Fresh Produce"
                  className="input-field text-xs py-1.5"
                  value={item.label || ""}
                  onChange={(e) => setItemField(idx, "label", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-400 mb-1">Icon</label>
                <select
                  className="input-field text-xs py-1.5"
                  value={item.icon || "Leaf"}
                  onChange={(e) => setItemField(idx, "icon", e.target.value)}
                >
                  {iconsList.map(ic => <option key={ic} value={ic}>{ic}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-400 mb-1">Background Gradient</label>
                <select
                  className="input-field text-xs py-1.5"
                  value={item.bg || "from-green-50 to-green-100"}
                  onChange={(e) => setItemField(idx, "bg", e.target.value)}
                >
                  {gradientsList.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                </select>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-xs text-gray-400 text-center py-2">No category cards defined.</p>}
      </div>
      <button
        type="button"
        onClick={add}
        className="w-full flex items-center justify-center gap-1.5 py-2 border border-dashed border-gray-200 hover:border-brand-primary rounded-xl text-xs font-semibold text-gray-600 hover:text-brand-primary bg-white transition-all"
      >
        <Plus className="w-4 h-4" /> Add Category Card
      </button>
    </div>
  );
}

function TimelineMilestonesRepeater({ value, onChange }) {
  const [items, setItems] = useState(() => parseJsonArray(value, []));

  useEffect(() => {
    const parsed = parseJsonArray(value);
    if (JSON.stringify(parsed) !== JSON.stringify(items)) setItems(parsed);
  }, [value]);

  const update = (updated) => {
    setItems(updated);
    onChange(JSON.stringify(updated));
  };

  const setItemField = (idx, field, val) => {
    const next = [...items];
    next[idx] = { ...next[idx], [field]: val };
    update(next);
  };

  const move = (idx, dir) => {
    const next = [...items];
    const targetIdx = idx + dir;
    if (targetIdx < 0 || targetIdx >= next.length) return;
    const temp = next[idx];
    next[idx] = next[targetIdx];
    next[targetIdx] = temp;
    update(next);
  };

  const remove = (idx) => update(items.filter((_, i) => i !== idx));
  const add = () => update([...items, { year: new Date().getFullYear().toString(), event: "" }]);

  return (
    <div className="space-y-3 border border-gray-150 rounded-2xl p-4 bg-gray-50/50">
      <div className="space-y-3">
        {items.map((item, idx) => (
          <div key={idx} className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-gray-400">Milestone #{idx + 1}</span>
              <div className="flex gap-0.5">
                <button type="button" disabled={idx === 0} onClick={() => move(idx, -1)} className="w-6 h-6 rounded bg-gray-50 hover:bg-gray-100 text-gray-500 disabled:opacity-30 flex items-center justify-center"><ArrowUp className="w-3.5 h-3.5" /></button>
                <button type="button" disabled={idx === items.length - 1} onClick={() => move(idx, 1)} className="w-6 h-6 rounded bg-gray-50 hover:bg-gray-100 text-gray-500 disabled:opacity-30 flex items-center justify-center"><ArrowDown className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => remove(idx)} className="w-6 h-6 rounded bg-red-50 hover:bg-red-100 text-red-500 flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            <div className="grid sm:grid-cols-4 gap-2">
              <div>
                <label className="block text-[10px] font-semibold text-gray-400 mb-1">Year</label>
                <input
                  type="text"
                  placeholder="e.g. 2008"
                  className="input-field text-xs py-1.5"
                  value={item.year || ""}
                  onChange={(e) => setItemField(idx, "year", e.target.value)}
                />
              </div>
              <div className="sm:col-span-3">
                <label className="block text-[10px] font-semibold text-gray-400 mb-1">Event / Achievement Description</label>
                <textarea
                  placeholder="Describe the milestone event..."
                  className="input-field text-xs py-1.5 resize-none"
                  rows={2}
                  value={item.event || ""}
                  onChange={(e) => setItemField(idx, "event", e.target.value)}
                />
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-xs text-gray-400 text-center py-2">No timeline milestones defined.</p>}
      </div>
      <button
        type="button"
        onClick={add}
        className="w-full flex items-center justify-center gap-1.5 py-2 border border-dashed border-gray-200 hover:border-brand-primary rounded-xl text-xs font-semibold text-gray-600 hover:text-brand-primary bg-white transition-all"
      >
        <Plus className="w-4 h-4" /> Add Milestone
      </button>
    </div>
  );
}

function BadgesRepeater({ value, onChange }) {
  const [items, setItems] = useState(() => parseJsonArray(value, []));

  useEffect(() => {
    const parsed = parseJsonArray(value);
    if (JSON.stringify(parsed) !== JSON.stringify(items)) setItems(parsed);
  }, [value]);

  const update = (updated) => {
    setItems(updated);
    onChange(JSON.stringify(updated));
  };

  const setItemField = (idx, field, val) => {
    const next = [...items];
    next[idx] = { ...next[idx], [field]: val };
    update(next);
  };

  const move = (idx, dir) => {
    const next = [...items];
    const targetIdx = idx + dir;
    if (targetIdx < 0 || targetIdx >= next.length) return;
    const temp = next[idx];
    next[idx] = next[targetIdx];
    next[targetIdx] = temp;
    update(next);
  };

  const remove = (idx) => update(items.filter((_, i) => i !== idx));
  const add = () => update([...items, { icon: "Globe", text: "New Badge" }]);

  const iconsList = ["Globe", "Award", "Truck", "Shield", "CheckCircle", "Star", "Leaf", "Users"];

  return (
    <div className="space-y-3 border border-gray-150 rounded-2xl p-4 bg-gray-50/50">
      <div className="space-y-2">
        {items.map((item, idx) => (
          <div key={idx} className="flex items-center gap-2 bg-white p-2 rounded-xl border border-gray-100 shadow-sm">
            <span className="text-xs font-bold text-gray-400 px-1">#{idx + 1}</span>
            <input
              type="text"
              placeholder="Badge Label"
              className="input-field text-xs py-1.5 flex-1"
              value={item.text || ""}
              onChange={(e) => setItemField(idx, "text", e.target.value)}
            />
            <select
              className="input-field text-xs py-1.5 w-36"
              value={item.icon || "Globe"}
              onChange={(e) => setItemField(idx, "icon", e.target.value)}
            >
              {iconsList.map(ic => <option key={ic} value={ic}>{ic}</option>)}
            </select>
            <div className="flex gap-0.5">
              <button type="button" disabled={idx === 0} onClick={() => move(idx, -1)} className="w-6 h-6 rounded bg-gray-50 hover:bg-gray-100 text-gray-500 disabled:opacity-30 flex items-center justify-center"><ArrowUp className="w-3.5 h-3.5" /></button>
              <button type="button" disabled={idx === items.length - 1} onClick={() => move(idx, 1)} className="w-6 h-6 rounded bg-gray-50 hover:bg-gray-100 text-gray-500 disabled:opacity-30 flex items-center justify-center"><ArrowDown className="w-3.5 h-3.5" /></button>
              <button type="button" onClick={() => remove(idx)} className="w-6 h-6 rounded bg-red-50 hover:bg-red-100 text-red-500 flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-xs text-gray-400 text-center py-2">No trust badges defined.</p>}
      </div>
      <button
        type="button"
        onClick={add}
        className="w-full flex items-center justify-center gap-1 py-1.5 border border-dashed border-gray-200 hover:border-brand-primary rounded-xl text-xs font-semibold text-gray-600 hover:text-brand-primary bg-white transition-all"
      >
        <Plus className="w-3.5 h-3.5" /> Add Trust Badge
      </button>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ADMIN SETTINGS
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
export function AdminSettings() {
  const [settings, setSettings] = useState({
    site_name:"Sava Fresh", site_tagline:"Trading FZE", site_description:"Premium global food trading company.",
    site_logo:"/logo.png", site_logo_dark:"/logo-dark.png",
    whatsapp_number:"+971501234567", call_number:"+971501234567", email:"info@savafresh.com",
    address:"Sharjah Airport International Free Zone (SAIF Zone), UAE",
    footer_text:"Connecting the world's finest food producers with global markets.",
    social_facebook:"", social_instagram:"", social_linkedin:"", social_twitter:"",
    header_cta_text:"Get in Touch",
    header_cta_link:"/contact",
    navigation_menu_json: JSON.stringify([
      { "label": "Home", "path": "/" },
      { "label": "About", "path": "/about", "hasDropdown": true, "submenu": [
        { "label": "About Us", "path": "/about", "icon": "Info" },
        { "label": "Our Services", "path": "/services", "icon": "Wrench" },
        { "label": "Gallery", "path": "/gallery", "icon": "Image" }
      ] },
      { "label": "Products", "path": "/products" },
      { "label": "Blog", "path": "/blog" },
      { "label": "Contact", "path": "/contact" }
    ], null, 2),
    footer_cta_heading:"Ready to source globally?",
    footer_cta_subheading:"Contact us for competitive pricing and reliable supply chains.",
    footer_cta_btn_text:"Send Enquiry",
    footer_cta_btn_link:"/contact",
    footer_company_menu_json: JSON.stringify([
      { "label": "About Us", "path": "/about" },
      { "label": "Our Services", "path": "/services" },
      { "label": "Products", "path": "/products" },
      { "label": "Gallery", "path": "/gallery" }
    ], null, 2),
    footer_info_menu_json: JSON.stringify([
      { "label": "Blog", "path": "/blog" },
      { "label": "Contact", "path": "/contact" },
      { "label": "Privacy Policy", "path": "/privacy-policy" },
      { "label": "Terms & Conditions", "path": "/terms" }
    ], null, 2),
    whatsapp_message:"Hello Sava Fresh, I am interested in your products."
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    settingsAPI.getAll().then(r => {
      if (r.data.settings) {
        setSettings(s => {
          const merged = { ...s };
          Object.entries(r.data.settings).forEach(([k, v]) => {
            if (v !== null && v !== undefined && v !== "") {
              merged[k] = v;
            }
          });
          return merged;
        });
      }
    }).catch(()=>{});
  }, []);

  const handleSave = async () => {
    setSaving(true); setSaved(false);
    try { await settingsAPI.update(settings); setSaved(true); setTimeout(()=>setSaved(false),3000); }
    catch { alert("Save failed"); }
    finally { setSaving(false); }
  };

  const set = (k) => (e) => setSettings(s=>({...s,[k]:e.target.value}));

  const sections = [
    { title: "Brand Identity", fields: [
      {k:"site_name",l:"Site / Brand Name",t:"text"},
      {k:"site_tagline",l:"Tagline",t:"text"},
      {k:"site_description",l:"Site Description",t:"textarea"},
      {k:"site_logo",l:"Header Logo (Light background)",t:"image"},
      {k:"site_logo_dark",l:"Footer Logo (Dark background)",t:"image"},
      {k:"site_favicon",l:"Website Favicon (PNG/ICO Icon)",t:"image"}
    ]},
    { title: "Navigation & Header", fields: [
      {k:"header_cta_text",l:"Header CTA Button Text",t:"text"},
      {k:"header_cta_link",l:"Header CTA Button Link",t:"text"},
      {k:"navigation_menu_json",l:"Navigation Menu Config (JSON Array)",t:"textarea"}
    ]},
    { title: "Contact & WhatsApp", fields: [
      {k:"whatsapp_number",l:"WhatsApp Number",t:"text",ph:"+971XXXXXXXXX"},
      {k:"call_number",l:"Call Number",t:"text",ph:"+971XXXXXXXXX"},
      {k:"email",l:"Email Address",t:"text"},
      {k:"address",l:"Business Address",t:"textarea"},
      {k:"whatsapp_message",l:"Default WhatsApp Enquiry Message",t:"textarea"},
      {k:"map_embed_url",l:"Google Maps Embed URL (iFrame Src)",t:"textarea",ph:"https://www.google.com/maps/embed?pb=..."}
    ]},
    { title: "Footer Options", fields: [
      {k:"footer_text",l:"Footer Description",t:"textarea"},
      {k:"footer_cta_heading",l:"Footer CTA Heading",t:"text"},
      {k:"footer_cta_subheading",l:"Footer CTA Subheading",t:"text"},
      {k:"footer_cta_btn_text",l:"Footer CTA Button Text",t:"text"},
      {k:"footer_cta_btn_link",l:"Footer CTA Button Link",t:"text"},
      {k:"footer_company_menu_json",l:"Company Links Column Config (JSON Array)",t:"textarea"},
      {k:"footer_info_menu_json",l:"Information Links Column Config (JSON Array)",t:"textarea"},
      {k:"copyright_text",l:"Copyright Text Template",t:"text",ph:"e.g. © {year} Sava Fresh. All rights reserved."},
      {k:"copyright_dev_name",l:"Developer Name / Credit",t:"text",ph:"e.g. Developed by Company"},
      {k:"copyright_dev_link",l:"Developer Link",t:"text",ph:"e.g. https://company.com"},
      {k:"copyright_logo",l:"Developer / Small Copyright Logo",t:"image"},
      {k:"copyright_links_menu_json",l:"Copyright Bottom Links (Privacy, Terms, etc.)",t:"textarea"}
    ]},
    { title: "Social Media", fields: [
      {k:"social_facebook",l:"Facebook URL",t:"text"},
      {k:"social_instagram",l:"Instagram URL",t:"text"},
      {k:"social_linkedin",l:"LinkedIn URL",t:"text"},
      {k:"social_twitter",l:"Twitter/X URL",t:"text"}
    ]},
  ];

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h2 className="font-heading font-bold text-gray-900 text-xl">Global Settings</h2><p className="text-gray-400 text-sm">Manage site-wide configuration</p></div>
        <div className="flex items-center gap-3">
          {saved && <span className="flex items-center gap-1.5 text-green-700 text-sm font-semibold bg-green-50 px-3 py-1.5 rounded-xl border border-green-200"><CheckCircle className="w-4 h-4" /> Saved!</span>}
          <button onClick={handleSave} disabled={saving} className="btn-primary px-5 py-2.5 text-sm">{saving ? <><Loader className="w-4 h-4 animate-spin" />Saving...</> : <><Save className="w-4 h-4" />Save All</>}</button>
        </div>
      </div>

      {sections.map(section => (
        <div key={section.title} className="bg-white rounded-2xl shadow-card p-6">
          <h3 className="font-heading font-semibold text-gray-900 mb-4">{section.title}</h3>
          <div className="space-y-4">
            {section.fields.map(field => {
              const value = settings[field.k] || "";
              const handleValChange = (val) => setSettings(s => ({ ...s, [field.k]: val }));
              return (
                <div key={field.k}>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">{field.l}</label>
                  {field.k === "navigation_menu_json" ? (
                    <NavigationMenuRepeater value={value} onChange={handleValChange} />
                  ) : field.k.includes("menu_json") ? (
                    <LinkListRepeater value={value} onChange={handleValChange} />
                  ) : field.t === "image" ? (
                    <div className="flex gap-2">
                      <input type="text" className="input-field text-sm flex-1" value={value} onChange={e => handleValChange(e.target.value)} placeholder={field.ph||""} />
                      <ImageUploadBtn onUploaded={url => handleValChange(url)} type="settings" />
                    </div>
                  ) : field.t === "textarea" ? (
                    <textarea className="input-field resize-none text-xs font-mono" rows={3} value={value} onChange={set(field.k)} />
                  ) : (
                    <input type="text" className="input-field" value={value} onChange={set(field.k)} placeholder={field.ph||""} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// ADMIN CMS EDITOR
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const CMS_PAGES = [
  { id:"home", label:"Home", sections:[
    { id:"hero", label:"Hero Section", fields:[
      {k:"heading",l:"Heading"},
      {k:"description",l:"Description",multi:true},
      {k:"imageUrl",l:"Hero Side / Fallback Image",t:"image"},
      {k:"videoUrl",l:"Hero Background/Banner Video (MP4 / WebM)",t:"video"},
      {k:"videoPoster",l:"Video Fallback Poster Image",t:"image"},
      {k:"videoEnabled",l:"Hero Video Autoplay (Enable/Disable)",t:"toggle"},
      {k:"cta_primary",l:"Primary Button Text"},
      {k:"cta_secondary",l:"Secondary Button Text"}
    ]},
    { id:"about", label:"About Strip", fields:[{k:"heading",l:"Section Heading"},{k:"description",l:"Description",multi:true},{k:"imageUrl",l:"About Side Image",t:"image"},{k:"point1",l:"Feature Point 1"},{k:"point2",l:"Feature Point 2"},{k:"point3",l:"Feature Point 3"}]},
    { id:"stats", label:"Stats Counters", fields:[{k:"stats_json",l:"Stats Config (JSON Array)",multi:true}]},
    { id:"advantages", label:"The Sava Fresh Advantages", fields:[{k:"advantages_json",l:"Advantages Config (JSON Array)",multi:true}]},
    { id:"brands", label:"Our Brands Section", fields:[
      {k:"heading",l:"Section Title"},
      {k:"description",l:"Section Description",multi:true}
    ]},
    { id:"cta", label:"CTA Section", fields:[
      {k:"heading",l:"CTA Title"},
      {k:"description",l:"CTA Description",multi:true},
      {k:"btn_primary_txt",l:"Primary Button Text"},
      {k:"btn_primary_link",l:"Primary Button Link"},
      {k:"btn_secondary_txt",l:"Secondary Button Text"},
      {k:"btn_secondary_link",l:"Secondary Button Link"}
    ]}
  ]},
  { id:"about", label:"About Page", sections:[
    { id:"hero", label:"Hero", fields:[{k:"heading",l:"Page Heading"},{k:"description",l:"Description",multi:true}]},
    { id:"story", label:"Our Story", fields:[{k:"heading",l:"Section Heading"},{k:"paragraph1",l:"Paragraph 1",multi:true},{k:"paragraph2",l:"Paragraph 2",multi:true},{k:"paragraph3",l:"Paragraph 3",multi:true},{k:"imageUrl",l:"Story Side Image",t:"image"}]},
    { id:"mission", label:"Mission", fields:[{k:"text",l:"Mission Statement",multi:true}]},
    { id:"vision", label:"Vision", fields:[{k:"text",l:"Vision Statement",multi:true}]},
    { id:"office", label:"Registered Office & Global Hub", fields:[
      {k:"heading",l:"Office Section Title"},
      {k:"address",l:"Registered Physical Address",multi:true},
      {k:"info",l:"Hub Overview & Sourcing Logistics",multi:true},
      {k:"imageUrl",l:"Office / Hub Image",t:"image"}
    ]},
    { id:"enhanced_section", label:"Enhanced Highlight Section", fields:[
      {k:"heading",l:"Section Heading (H2)"},
      {k:"content",l:"Section Content",multi:true},
      {k:"imageUrl",l:"Section Side Image",t:"image"},
      {k:"cta_text",l:"CTA Button Text"},
      {k:"cta_link",l:"CTA Link URL"}
    ]},
    { id:"values", label:"Core Values", fields:[
      {k:"label",l:"Section Label"},
      {k:"heading",l:"Section Heading"},
      {k:"values_json",l:"Values JSON Array",multi:true}
    ]},
    { id:"cta", label:"Partner CTA Section", fields:[
      {k:"heading",l:"CTA Title"},
      {k:"description",l:"CTA Description",multi:true},
      {k:"btn_txt",l:"CTA Button Text"},
      {k:"btn_link",l:"CTA Button Link"}
    ]}
  ]},
  { id:"services", label:"Services Page", sections:[
    { id:"hero", label:"Hero", fields:[{k:"heading",l:"Page Heading"},{k:"description",l:"Description",multi:true}]},
    { id:"enhanced_section", label:"Enhanced Sourcing Section", fields:[
      {k:"heading",l:"Section Heading (H2)"},
      {k:"content",l:"Section Content",multi:true},
      {k:"imageUrl",l:"Section Side Image",t:"image"},
      {k:"cta_text",l:"CTA Button Text"},
      {k:"cta_link",l:"CTA Link URL"}
    ]}
  ]},
  { id:"products", label:"Products Page", sections:[
    { id:"hero", label:"Hero", fields:[{k:"heading",l:"Page Heading"},{k:"description",l:"Description",multi:true},{k:"imageUrl",l:"Hero Side Image",t:"image"}]},
    { id:"badges", label:"Trust Badges", fields:[{k:"badges_json",l:"Badges JSON Array",multi:true}]}
  ]},
  { id:"blog", label:"Blog Page", sections:[
    { id:"hero", label:"Hero", fields:[{k:"heading",l:"Page Heading"},{k:"description",l:"Description",multi:true}]}
  ]},
  { id:"gallery", label:"Gallery Page", sections:[
    { id:"hero", label:"Hero", fields:[{k:"heading",l:"Page Heading"},{k:"description",l:"Description",multi:true}]}
  ]},
  { id:"contact", label:"Contact Page", sections:[
    { id:"hero", label:"Hero", fields:[{k:"heading",l:"Page Heading"},{k:"description",l:"Description",multi:true}]},
    { id:"sidebar", label:"Sidebar Info Card Subtitles", fields:[
      {k:"phone_sub",l:"Phone Card Subtext"},
      {k:"email_sub",l:"Email Card Subtext"},
      {k:"address_sub",l:"Address Card Subtext"}
    ]},
    { id:"expect", label:"Expect Info Box", fields:[
      {k:"heading",l:"Expect Box Title"},
      {k:"point1",l:"Bullet Point 1"},
      {k:"point2",l:"Bullet Point 2"},
      {k:"point3",l:"Bullet Point 3"},
      {k:"point4",l:"Bullet Point 4"}
    ]}
  ]},
  { id:"privacy", label:"Privacy Policy", sections:[
    { id:"main", label:"Content", fields:[{k:"content",l:"Full Content (HTML supported)",multi:true,rows:15}]},
  ]},
  { id:"terms", label:"Terms & Conditions", sections:[
    { id:"main", label:"Content", fields:[{k:"content",l:"Full Content (HTML supported)",multi:true,rows:15}]},
  ]},
];

const CMS_FALLBACKS = {
  home: {
    hero: {
      heading: "Premium Quality Food Trading Worldwide",
      description: "Sava Fresh Trading FZE connects the world's finest agricultural producers with global markets. Trusted sourcing, certified quality, worldwide delivery.",
      imageUrl: "https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=800&q=80",
      videoUrl: "",
      videoPoster: "",
      videoEnabled: "false",
      cta_primary: "Explore Products",
      cta_secondary: "Get in Touch"
    },
    about: {
      heading: "Global Trading Excellence",
      description: "With deep expertise in international food trade, Sava Fresh bridges quality producers and global buyers with unmatched reliability.",
      imageUrl: "https://images.unsplash.com/photo-1578575437130-527eed3abbec?auto=format&fit=crop&w=800&q=80",
      point1: "Direct relationships with 200+ certified producers worldwide",
      point2: "Full cold-chain logistics and quality control at every step",
      point3: "Regulatory compliance for all major import/export markets"
    },
    stats: {
      stats_json: JSON.stringify([
        { "value": 50, "suffix": "+", "label": "Countries Served", "icon": "Globe" },
        { "value": 500, "suffix": "+", "label": "Products Sourced", "icon": "Package" },
        { "value": 200, "suffix": "+", "label": "Global Clients", "icon": "Users" },
        { "value": 15, "suffix": "+", "label": "Years Experience", "icon": "TrendingUp" }
      ], null, 2)
    },
    advantages: {
      advantages_json: JSON.stringify([
        { "icon": "Shield", "title": "Certified Quality", "desc": "Every product meets international food safety and quality standards with full traceability.", "color": "brand" },
        { "icon": "Globe", "title": "Global Network", "desc": "Direct relationships with producers across Asia, Africa, Europe, and the Americas.", "color": "gold" },
        { "icon": "Truck", "title": "Reliable Logistics", "desc": "End-to-end supply chain management with temperature-controlled shipping worldwide.", "color": "brand" },
        { "icon": "Award", "title": "Trusted Partner", "desc": "Over 15 years of trusted partnerships with buyers, retailers, and food manufacturers.", "color": "gold" },
        { "icon": "Leaf", "title": "Fresh & Natural", "desc": "Committed to sourcing the freshest, most natural products with minimal processing.", "color": "brand" },
        { "icon": "CheckCircle", "title": "Full Compliance", "desc": "Complete regulatory compliance for import/export across all major global markets.", "color": "gold" }
      ], null, 2)
    },
    brands: {
      heading: "Trusted Brands in Our Portfolio",
      description: "Partnering with leading brands and producers worldwide to deliver excellence."
    },
    cta: {
      heading: "Ready to Source Globally?",
      description: "Join 200+ businesses worldwide who trust Sava Fresh for their food sourcing needs.",
      btn_primary_txt: "Send Enquiry",
      btn_primary_link: "/contact",
      btn_secondary_txt: "Browse Products",
      btn_secondary_link: "/products"
    }
  },
  about: {
    hero: {
      heading: "About Sava Fresh Trading FZE",
      description: "Sava Fresh Trading FZE is a premium global food trading company headquartered in the Sharjah Airport International Free Zone (SAIF Zone), UAE. With over a decade of experience in international food trade, we have built a reputation for quality, reliability, and transparent business practices. We connect certified agricultural producers with buyers across 50+ countries, offering a diverse range of food commodities with full supply chain support."
    },
    story: {
      heading: "Built on Trust, Driven by Quality",
      paragraph1: "Founded with a vision to transform global food trade, Sava Fresh began as a regional food trading enterprise and has grown into a respected international trading company. Our journey is marked by strategic partnerships, quality certifications, and a relentless commitment to excellence at every stage of the supply chain.",
      paragraph2: "Our deep relationships with certified producers across Asia, Africa, Europe, and the Americas allow us to source the finest quality food products at competitive prices. We handle everything from procurement and quality inspection to logistics, documentation, and final delivery — giving our clients a seamless, worry-free experience.",
      paragraph3: "Today, we serve a diverse portfolio of clients including retail chains, wholesale distributors, food manufacturers, hotel groups, and government institutions across 50+ countries. Our growing team of trade specialists, quality assurance experts, and logistics professionals ensures every shipment meets the highest standards.",
      imageUrl: "https://images.unsplash.com/photo-1521791136368-1a46827d0af1?auto=format&fit=crop&w=800&q=80"
    },
    mission: {
      text: "To be the world's most trusted food trading partner — connecting certified producers with global buyers through transparent, ethical, and efficient trade practices. We are committed to delivering premium quality products, ensuring complete regulatory compliance, and fostering long-term partnerships built on mutual respect and shared success."
    },
    vision: {
      text: "To become the leading global food trading company recognized for exceptional quality, ethical sourcing, and innovative supply chain solutions. We envision a future where Sava Fresh is the first choice for businesses seeking reliable, sustainable, and premium food products from around the world — setting new benchmarks for the industry."
    },
    values: {
      label: "Our Values",
      heading: "What We Stand For",
      values_json: JSON.stringify([
        { "icon": "Award", "title": "Quality First", "desc": "We maintain uncompromising quality standards at every step of the supply chain. From supplier qualification to final delivery, quality is never compromised — it is our defining commitment.", "color": "brand" },
        { "icon": "Globe", "title": "Global Reach", "desc": "Our network spans 50+ countries across five continents. We leverage these global connections to source the finest products and deliver them to any market, efficiently and reliably.", "color": "gold" },
        { "icon": "Users", "title": "Partnership", "desc": "We build long-term relationships grounded in trust, transparency, and shared goals. Whether you are a producer or a buyer, we treat every partner as a valued member of the Sava Fresh family.", "color": "brand" }
      ], null, 2)
    },
    office: {
      heading: "Registered Office & Global Trading Hub",
      address: "Sava Fresh Trading FZE\nJebel Ali Free Zone (JAFZA)\nDubai, United Arab Emirates",
      info: "Strategically located at the crossroads of international food commerce, our headquarters in JAFZA serves as the nucleus for global trading, supply chain management, and regulatory coordination across GCC, Asia, Europe, and Africa.",
      imageUrl: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=800&q=80"
    },
    cta: {
      heading: "Partner With Sava Fresh",
      description: "Join our growing network of global buyers and suppliers. Experience the difference that quality, reliability, and expertise can make for your business.",
      btn_txt: "Get in Touch",
      btn_link: "/contact"
    }
  },
  services: {
    hero: {
      heading: "End-to-End Food Trading Solutions",
      description: "From sourcing and procurement to logistics and compliance — Sava Fresh offers comprehensive food trading services that cover every aspect of the international supply chain. Our experienced team handles the complexity so you can focus on your core business. We provide customized solutions for importers, exporters, retailers, and food manufacturers worldwide."
    }
  },
  products: {
    hero: {
      heading: "Premium Food Products, Globally Sourced",
      description: "Explore our extensive range of premium food products carefully sourced from certified producers across five continents. From staple grains and fresh produce to frozen foods and edible oils — every product in our portfolio meets strict quality and safety standards. We offer flexible packaging, bulk pricing, and full documentation support for buyers worldwide.",
      imageUrl: "https://images.unsplash.com/photo-1586201375761-83865001e31c?auto=format&fit=crop&w=800&q=80"
    },
    badges: {
      badges_json: JSON.stringify([
        { "icon": "Globe", "text": "50+ Countries" },
        { "icon": "Award", "text": "ISO Certified" },
        { "icon": "Truck", "text": "Global Delivery" }
      ], null, 2)
    }
  },
  blog: {
    hero: {
      heading: "Industry Insights & Market Intelligence",
      description: "Stay ahead in the global food trade with expert analysis, market reports, sourcing guides, and company updates. Our articles help buyers, importers, and distributors make smarter decisions in an ever-changing global market. From commodity price trends to regulatory updates, we cover what matters most for your supply chain decisions."
    }
  },
  gallery: {
    hero: {
      heading: "Our Visual Story",
      description: "Explore our products, facilities, and global operations through our photo gallery."
    }
  },
  contact: {
    hero: {
      heading: "Let's Start a Conversation",
      description: "Whether you need a bulk quote, product specifications, or want to explore a long-term supply partnership — our team is ready to assist. Reach out through any channel that works best for you, and we promise a prompt, professional response."
    },
    sidebar: {
      phone_sub: "Available during business hours",
      email_sub: "We respond within 24 hours",
      address_sub: "Free Zone, UAE"
    },
    expect: {
      heading: "What to expect",
      point1: "Response within 24 business hours",
      point2: "Detailed product information & pricing",
      point3: "Custom quotes for bulk orders",
      point4: "Free samples for qualified buyers"
    }
  },
  privacy: {
    main: {
      content: `<h2>Privacy Policy</h2><p>Last updated: January 2024</p><p>At Sava Fresh Trading FZE ("we," "us," or "our"), we are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or contact us for business purposes.</p><h3>Information We Collect</h3><p>We collect information you provide directly to us, including your name, email address, phone number, company name, and any message content when you fill out our contact form or communicate with us via email or phone...</p>`
    }
  },
  terms: {
    main: {
      content: `<h2>Terms and Conditions</h2><p>Last updated: January 2024</p><p>By accessing and using the Sava Fresh Trading FZE website (savafresh.com), you accept and agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our website...</p>`
    }
  }
};

export function AdminCMS() {
  const [activePage, setActivePage] = useState(CMS_PAGES[0]);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setLoading(true); setData({});
    cmsAPI.getPage(activePage.id)
      .then(r => {
        const flat = {};
        
        // 1. Load client-side fallbacks first
        const fallbacks = CMS_FALLBACKS[activePage.id] || {};
        Object.entries(fallbacks).forEach(([sec, fields]) => {
          if (!flat[sec]) flat[sec] = {};
          Object.entries(fields).forEach(([k, val]) => {
            flat[sec][k] = val;
          });
        });

        // 2. Overlay values from DB
        Object.entries(r.data.content || {}).forEach(([sec, fields]) => {
          Object.entries(fields).forEach(([k, val]) => {
            if (!flat[sec]) flat[sec] = {};
            if (val.value !== null && val.value !== undefined) {
              flat[sec][k] = val.value;
            }
          });
        });
        setData(flat);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [activePage.id]);

  const handleChange = (section, key, value) => setData(prev => ({...prev,[section]:{...(prev[section]||{}),[key]:value}}));

  const handleSave = async () => {
    setSaving(true); setSaved(false);
    try {
      const payload = {};
      Object.entries(data).forEach(([section, fields]) => {
        Object.entries(fields).forEach(([key, value]) => {
          const valStr = value !== null && value !== undefined ? String(value) : "";
          const encoded = "base64:" + btoa(unescape(encodeURIComponent(valStr)));
          payload[`${section}.${key}`] = { value: encoded, type: "text" };
        });
      });
      // Synchronize video keys between camelCase and snake_case so DB is always 100% consistent
      if (activePage.id === "home" && data.hero) {
        const vUrl = data.hero.videoUrl || "";
        const vEn = String(data.hero.videoEnabled ?? "false");
        const vPoster = data.hero.videoPoster || "";
        payload["hero.video_url"] = { value: "base64:" + btoa(unescape(encodeURIComponent(vUrl))), type: "text" };
        payload["hero.video_enabled"] = { value: "base64:" + btoa(unescape(encodeURIComponent(vEn))), type: "text" };
        payload["hero.video_poster"] = { value: "base64:" + btoa(unescape(encodeURIComponent(vPoster))), type: "text" };
      }
      await cmsAPI.updatePage(activePage.id, payload);
      setSaved(true); setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error(err);
      alert("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h2 className="font-heading font-bold text-gray-900 text-xl">CMS Editor</h2><p className="text-gray-400 text-sm">Edit page content dynamically</p></div>
        <div className="flex items-center gap-3">
          {saved && <span className="flex items-center gap-1.5 text-green-700 text-sm font-semibold bg-green-50 px-3 py-1.5 rounded-xl border border-green-200"><CheckCircle className="w-4 h-4" /> Saved!</span>}
          <button onClick={handleSave} disabled={saving} className="btn-primary text-sm px-5 py-2.5">
            {saving ? <><Loader className="w-4 h-4 animate-spin" />Saving...</> : <><Save className="w-4 h-4" />Save Changes</>}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-4 gap-5">
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl shadow-card p-3 sticky top-24">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-2 mb-3">Pages</p>
            {CMS_PAGES.map(page => (
              <button key={page.id} onClick={() => setActivePage(page)}
                className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium transition-all text-left mb-1 ${activePage.id===page.id ? "bg-brand-pale text-brand-primary" : "text-gray-500 hover:bg-gray-50 hover:text-gray-800"}`}>
                {page.label}
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-3 space-y-4">
          {loading ? <div className="flex items-center justify-center py-20 bg-white rounded-2xl shadow-card"><Loader className="w-6 h-6 text-brand-primary animate-spin mr-2" /><span className="text-gray-400 text-sm">Loading...</span></div>
          : activePage.sections.map(section => (
            <div key={section.id} className="bg-white rounded-2xl shadow-soft border border-gray-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
                <h3 className="font-semibold text-gray-800 text-sm">{section.label}</h3>
              </div>
              <div className="p-5 grid sm:grid-cols-2 gap-4">
                {section.fields.map(field => {
                  const val = data?.[section.id]?.[field.k] || "";
                  const onChange = (v) => handleChange(section.id, field.k, v);
                  
                  let repeaterComponent = null;
                  if (field.k === "stats_json") {
                    repeaterComponent = <StatsRepeater value={val} onChange={onChange} />;
                  } else if (field.k === "advantages_json" || field.k === "values_json") {
                    repeaterComponent = <CardListRepeater value={val} onChange={onChange} />;
                  } else if (field.k === "cards_json") {
                    repeaterComponent = <CategoryCardsRepeater value={val} onChange={onChange} />;
                  } else if (field.k === "timeline_json") {
                    repeaterComponent = <TimelineMilestonesRepeater value={val} onChange={onChange} />;
                  } else if (field.k === "badges_json") {
                    repeaterComponent = <BadgesRepeater value={val} onChange={onChange} />;
                  }

                  return (
                    <div key={field.k} className={field.multi || field.t === "image" || field.t === "video" || repeaterComponent ? "sm:col-span-2" : ""}>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">{field.l}</label>
                      {repeaterComponent ? (
                        repeaterComponent
                      ) : field.t === "image" ? (
                        <div className="space-y-2">
                          <div className="flex gap-2">
                            <input className="input-field text-sm flex-1" value={val} onChange={e=>onChange(e.target.value)} placeholder="Image URL..." />
                            <ImageUploadBtn onUploaded={url=>onChange(url)} type="cms" />
                          </div>
                          {val && (
                            <img src={getImageUrl(val)} alt="" className="h-20 w-auto rounded-xl object-cover border border-gray-100" onError={e=>e.target.style.display="none"} />
                          )}
                        </div>
                      ) : field.t === "video" ? (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <input className="input-field text-sm flex-1" value={val} onChange={e=>onChange(e.target.value)} placeholder="Video URL (MP4 / WebM)..." />
                            <VideoUploadBtn onUploaded={url => {
                              onChange(url);
                              handleChange(section.id, "videoEnabled", "true");
                            }} />
                            {val ? (
                              <button
                                type="button"
                                onClick={() => {
                                  onChange("");
                                  handleChange(section.id, "videoEnabled", "false");
                                }}
                                className="px-3 py-2 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-xl transition-colors flex items-center gap-1 shrink-0"
                                title="Delete / Remove Video"
                              >
                                <Trash2 className="w-3.5 h-3.5" /> Remove Video
                              </button>
                            ) : null}
                          </div>
                          {val ? (
                            <div className="mt-2 max-w-md rounded-xl overflow-hidden border border-gray-200 bg-black">
                              <video src={getImageUrl(val)} controls className="w-full h-36 object-contain" />
                            </div>
                          ) : (
                            <p className="text-xs text-gray-400 italic">No video selected. Hero fallback image will be displayed on the homepage.</p>
                          )}
                        </div>
                      ) : field.t === "toggle" ? (
                        <div className="flex items-center gap-3 pt-2">
                          <input
                            type="checkbox"
                            id={`toggle-${section.id}-${field.k}`}
                            checked={val === "true" || val === true}
                            onChange={e => onChange(e.target.checked ? "true" : "false")}
                            className="w-4 h-4 accent-brand-primary cursor-pointer"
                          />
                          <label htmlFor={`toggle-${section.id}-${field.k}`} className="text-sm font-medium text-gray-700 cursor-pointer">
                            Enable {field.l}
                          </label>
                        </div>
                      ) : field.k.endsWith("_json") ? (
                        <textarea className="input-field resize-none text-xs font-mono" rows={8} value={val} onChange={e=>onChange(e.target.value)} placeholder="JSON Array..." />
                      ) : field.multi ? (
                        <WysiwygEditor value={val} onChange={onChange} />
                      ) : (
                        <input className="input-field text-sm" value={val} onChange={e=>onChange(e.target.value)} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
